<template>
  <div class="task-sidebar">
    <div class="task-body">
      <Menu>
        <MenuLabel>
          Priority
          <TextLink
            v-if="hasPriorities"
            @click="resetPriorities"
          >
            Reset
          </TextLink>
        </MenuLabel>
        <MenuList>
          <MenuItem
            v-for="item in priorityItems"
            :key="item.id"
            :data-key="item.id"
            :icon="item.icon"
            :variant="item.variant"
            :active="hasPriority(item.value)"
            @click="togglePriority(item.value)"
          >
            {{ item.label }}
          </MenuItem>
        </MenuList>
      </Menu>

      <MenuDivider />

      <Menu>
        <MenuLabel>
          Date
          <TextLink
            v-if="hasDates"
            @click="resetDates"
          >
            Reset
          </TextLink>
        </MenuLabel>
        <MenuList>
          <MenuItem
            icon="fas fa-exclamation-triangle"
            variant="danger"
            :active="localFilters.overdue"
            @click="toggleOverdue"
          >
            Overdue
          </MenuItem>
        </MenuList>
      </Menu>

      <div class="dates">
        <p>Before</p>
        <TextInput
          v-model="localFilters.dueDateBefore"
          type="date"
        />
        <p>After</p>
        <TextInput
          v-model="localFilters.dueDateAfter"
          type="date"
        />
      </div>

      <MenuDivider />

      <!--
      <Menu>
        <MenuLabel>
          Assignee
          <TextLink
            v-if="hasAssignees"
            @click="resetAssignees"
          >
            Reset
          </TextLink>
        </MenuLabel>
        <MenuItem
          v-for="assignee in assignees"
          :key="assignee.id"
          :data-key="assignee.id"
          :active="hasAssignee(assignee)"
          @click="toggleAssignee(assignee)"
        >
          {{ assignee.name }}
        </MenuItem>
      </Menu> -->

      <Menu>
        <MenuLabel>
          Assignee
          <TextLink
            v-if="hasAssignees"
            @click="resetAssignees"
          >
            Reset
          </TextLink>
        </MenuLabel>

        <AutocompleteWithFetch
          v-slot="{ item }"
          endpoint="/api/v1/advisors/"
          results-field="advisors"
          :multiple="true"
          @selection-change="handleAssigneeSelectionChange"
        >
          <div>
            <!-- Custom html for list items -->
            <p>{{ item.name }}</p>
          </div>
        </AutocompleteWithFetch>
      </Menu>

      <MenuDivider />

      <!--
      <Menu>
        <MenuLabel>
          Person
          <TextLink
            v-if="hasEntities"
            @click="resetEntities"
          >
            Reset
          </TextLink>
        </MenuLabel>
        <MenuItem
          v-for="person in people"
          :key="person.external_id"
          :data-key="person.external_id"
          :active="hasEntity(person)"
          @click="toggleEntity(person)"
        >
          {{ person.name }}
        </MenuItem>
      </Menu> -->

      <Menu>
        <MenuLabel>
          Person
          <TextLink
            v-if="hasEntities"
            @click="resetEntities"
          >
            Reset
          </TextLink>
        </MenuLabel>

        <AutocompleteWithFetch
          v-slot="{ item }"
          endpoint="/api/v1/entities/get_all/"
          results-field="items"
          :multiple="true"
          @selection-change="handleEntitySelectionChange"
        >
          <div>
            <!-- Custom html for list items -->
            <p>{{ item.name }}</p>
          </div>
        </AutocompleteWithFetch>
      </Menu>
    </div>

    <Stack
      v-if="hasChanges"
      class="task-footer"
      gap="2"
    >
      <Button
        variant="primary"
        size="5"
        :disabled="isLoading"
        @click="handleApply"
      >
        Apply filters
      </Button>
      <Button
        :disabled="isLoading"
        clickable
        @click="handleCancel"
      >
        Cancel
      </Button>
    </Stack>
  </div>
</template>

<script setup>
import { computed, defineEmits, ref, watch } from 'vue';
import { Button, Menu, MenuList, MenuItem, MenuLabel, MenuDivider, Stack, TextInput, TextLink } from '@stakeui';
import { hasFilterValue, resetFilterArray, toggleBooleanFilter, toggleFilterValue } from '../filterUtils.js';
import { useTaskStore } from '@frontend/ui/stores/taskStore.js';

import AutocompleteWithFetch from '@frontend/ui/components/utils/AutocompleteWithFetch.vue';


defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const taskStore = useTaskStore();
const localFilters = ref({});
const emit = defineEmits(['close']);

watch(
    () => taskStore.tasks.filters,
    newFilters => {
      localFilters.value = JSON.parse(JSON.stringify(newFilters));
    },
    { deep: true, immediate: true }, // immediate ensures it runs once on mount
);

const hasChanges = computed(() => JSON.stringify(localFilters.value) !== JSON.stringify(taskStore.tasks.filters));

// Priorities
const priorityItems = computed(() => [
  {
    id: 'priority-critical',
    value: 'critical',
    icon: 'fas fa-exclamation-triangle',
    label: 'Critical',
    count: 31,
    variant: 'danger',
  },
  {
    id: 'priority-high',
    value: 'high',
    icon: 'fas fa-exclamation',
    label: 'High',
    count: 18,
    variant: 'warning',
  },
  {
    id: 'priority-medium',
    value: 'medium',
    icon: 'fas fa-circle',
    label: 'Medium',
    count: 6,
    variant: 'info',
  },
  {
    id: 'priority-low',
    value: 'low',
    icon: 'far fa-circle',
    label: 'Low',
    count: 47,
    variant: 'success',
  },
]);

const hasPriorities = computed(() => {
  return localFilters.value.priorities?.length > 0;
});

const hasPriority = priority => {
  return hasFilterValue(localFilters.value, 'priorities', priority);
};

const resetPriorities = () => {
  resetFilterArray(localFilters.value, 'priorities');
};

const togglePriority = priority => {
  toggleFilterValue(localFilters.value, 'priorities', priority);
};

// Date
const toggleOverdue = () => {
  toggleBooleanFilter(localFilters.value, 'overdue');
};

const hasDates = computed(() => {
  return localFilters.value.overdue || localFilters.value.dueDateBefore !== '' || localFilters.value.dueDateAfter !== '';
});

const resetDates = () => {
  localFilters.value.overdue = false;
  localFilters.value.dueDateBefore = '';
  localFilters.value.dueDateAfter = '';
};

// Assignees
const toggleAssignee = assignee => {
  toggleFilterValue(localFilters.value, 'assignees', assignee);
};

const hasAssignee = assignee => {
  return hasFilterValue(localFilters.value, 'assignees', assignee);
};

const hasAssignees = computed(() => {
  const assigneesArray = localFilters.value?.tasks?.filters?.assignees;
  return Array.isArray(assigneesArray) && assigneesArray.length > 0;
});

const resetAssignees = () => {
  resetFilterArray(localFilters.value, 'assignees');
};

const assignees = computed(() => {
  return taskStore.tasks.advisorsWithTasks || [];
});

// People
const people = computed(() => taskStore.tasks?.entitiesWithTasks || []);

const hasEntities = computed(() => {
  return localFilters.value.entitiesWithTasks?.length > 0;
});

const handleAssigneeSelectionChange = selected => {
  let assignees;
  if (!selected) {
    assignees = [];
    //
  } else {
    assignees = selected.map(({ id, name }) => ({ id, name }));
  }

  localFilters.value = { ...localFilters.value, assignees };
};

const handleEntitySelectionChange = selected => {
  let entities;
  if (!selected) {
    entities = [];
    //
  } else {
    entities = selected.map(({ external_id, name }) => ({ external_id, name }));
  }

  localFilters.value = { ...localFilters.value, entities };
};

const toggleEntity = entity => {
  toggleFilterValue(localFilters.value, 'entities', entity);
};

const hasEntity = entity => {
  return hasFilterValue(localFilters.value, 'entities', entity);
};

const resetEntities = () => {
  resetFilterArray(localFilters.value, 'entities');
};

// Events
const handleApply = () => {
  taskStore.tasks.filters = JSON.parse(JSON.stringify(localFilters.value));
  taskStore.fetchTasks();
  emit('close');
};

const handleCancel = () => {
  localFilters.value = JSON.parse(JSON.stringify(taskStore.tasks.filters));
};
</script>

<style scoped>
.task-sidebar {
  display: flex;
  flex-direction: column;
}

.task-body {
  flex-grow: 1;
  padding: 1em;
}

.task-footer {
  background-color: #fff;
  padding: 0.5em;
  position: sticky;
  bottom: 0;
  border-top: 1px solid var(--stk-border);
}

.dates {
  color: var(--stk-text-weak);
  display: grid;
  font-size: 0.75em;
  grid-template-columns: 7em 1fr;
  gap: 0.25em 0.5em;
  margin-top: 0.5em;
  align-items: center;
}
</style>
